import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import StarRating from "./StarRating";

interface RatingFormProps {
  outfitId: string;
  initialRating?: number;
  onSubmit: (outfitId: string, rating: number) => Promise<void>;
  onCancel?: () => void;
}

/**
 * Rating submission form component
 */
const RatingForm: React.FC<RatingFormProps> = ({
  outfitId,
  initialRating = 0,
  onSubmit,
  onCancel,
}) => {
  const [rating, setRating] = useState<number>(initialRating);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  // Reset form when outfitId changes
  useEffect(() => {
    setRating(initialRating);
    setError(null);
    setSuccess(false);
  }, [outfitId, initialRating]);

  // Handle rating change
  const handleRatingChange = (value: number) => {
    setRating(value);
    setError(null);
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate rating
    if (rating <= 0) {
      setError("Please select a rating");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // Submit rating
      await onSubmit(outfitId, rating);

      // Show success message
      setSuccess(true);

      // Reset success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (error) {
      setError("Failed to submit rating. Please try again.");
      console.error("Rating submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 2, maxWidth: 400 }}>
      <Typography variant="h6" gutterBottom>
        Rate this Outfit
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Rating submitted successfully!
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mb: 2,
        }}
      >
        <StarRating
          value={rating}
          onChange={handleRatingChange}
          size="large"
          showLabel
        />

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Tap a star to rate
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || rating <= 0}
          startIcon={
            isSubmitting && <CircularProgress size={20} color="inherit" />
          }
        >
          {isSubmitting ? "Submitting..." : "Submit Rating"}
        </Button>
      </Box>
    </Paper>
  );
};

export default RatingForm;
